import { useState } from "react";

export interface Config {
  polling_interval_minutes: number;
  fit_threshold: number;
  model_score: string;
  model_tailor: string;
  model_extract: string;
  schedule_enabled: boolean;
  job_search_description: string;
  work_modes: string[];
  min_salary: number;
}

export interface PromptDef {
  name: string;
  label: string;
  default: string;
  custom: string | null;
  updated_at: string | null;
}

const WORK_MODES: { value: string; label: string }[] = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
];

export interface SecretStatus {
  configured: boolean;
  last_updated?: string;
  secret_arn_present?: boolean;
  aws_last_changed?: string;
  global?: boolean;
}

export function Section({ title, blurb, children }: { title: string; blurb?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-5">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h2>
        {blurb && <p className="mt-1 text-xs text-slate-500">{blurb}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function ConfiguredAt({ at }: { at?: string }) {
  if (!at) return null;
  return <p className="text-xs text-slate-500">Last updated: {new Date(at).toLocaleString()}</p>;
}

export function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type={type}
        autoComplete={type === "password" ? "new-password" : "off"}
        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function NumField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

export function ConfigCard({
  config,
  onSave,
  saving,
}: {
  config: Config;
  onSave: (c: Partial<Config>) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState(config);

  return (
    <Section title="Pipeline config" blurb="Adjust scoring threshold, polling cadence, and model picks.">
      <div className="grid grid-cols-2 gap-3">
        <NumField
          label="Polling interval (minutes)"
          value={draft.polling_interval_minutes}
          min={15}
          max={1440}
          onChange={(v) => setDraft({ ...draft, polling_interval_minutes: v })}
        />
        <NumField
          label="Fit threshold (0–100)"
          value={draft.fit_threshold}
          min={0}
          max={100}
          onChange={(v) => setDraft({ ...draft, fit_threshold: v })}
        />
        <Field label="Score model" value={draft.model_score} onChange={(v) => setDraft({ ...draft, model_score: v })} />
        <Field label="Tailor model" value={draft.model_tailor} onChange={(v) => setDraft({ ...draft, model_tailor: v })} />
        <Field label="Extract model" value={draft.model_extract} onChange={(v) => setDraft({ ...draft, model_extract: v })} />
        <label className="flex items-center gap-2 self-end pb-1.5 text-sm text-slate-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-600 bg-slate-950"
            checked={draft.schedule_enabled}
            onChange={(e) => setDraft({ ...draft, schedule_enabled: e.target.checked })}
          />
          Enable cron schedule (M7)
        </label>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          onClick={() => onSave(draft)}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save config"}
        </button>
      </div>
    </Section>
  );
}

export function ImapCard({
  status,
  onSave,
  saving,
}: {
  status: SecretStatus;
  onSave: (p: Record<string, string | number>) => void;
  saving: boolean;
}) {
  const [host, setHost] = useState("imap.mail.yahoo.com");
  const [port, setPort] = useState(993);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");

  return (
    <Section
      title={`Yahoo IMAP — ${status.configured ? "configured" : "not set"}`}
      blurb="Use a Yahoo app password (Account Security → Generate app password). The agent polls UNSEEN mail from linkedin.com."
    >
      <ConfiguredAt at={status.last_updated} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="IMAP host" value={host} onChange={setHost} />
        <NumField label="Port" value={port} min={1} max={65535} onChange={setPort} />
        <Field label="Username (Yahoo email)" value={user} onChange={setUser} />
        <Field label="App password" value={password} onChange={setPassword} type="password" />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          disabled={saving || !user || !password}
          onClick={() => {
            onSave({ host, port, user, password });
            setPassword("");
          }}
        >
          {saving ? "Saving…" : "Save IMAP"}
        </button>
      </div>
    </Section>
  );
}

export function LinkedInCard({
  status,
  onSave,
  saving,
}: {
  status: SecretStatus;
  onSave: (p: Record<string, string | number>) => void;
  saving: boolean;
}) {
  const [liAt, setLiAt] = useState("");
  const [jsessionid, setJsessionid] = useState("");

  return (
    <Section
      title={`LinkedIn session cookie — ${status.configured ? "configured" : "not set"}`}
      blurb="From DevTools → Application → Cookies → linkedin.com. Copy the value of `li_at`. Refresh ~every 30 days."
    >
      <ConfiguredAt at={status.last_updated} />
      <Field label="li_at cookie value" value={liAt} onChange={setLiAt} type="password" />
      <Field label="JSESSIONID (optional)" value={jsessionid} onChange={setJsessionid} />
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          disabled={saving || !liAt}
          onClick={() => {
            onSave({ li_at: liAt, jsessionid });
            setLiAt("");
            setJsessionid("");
          }}
        >
          {saving ? "Saving…" : "Save cookie"}
        </button>
      </div>
    </Section>
  );
}

export function JobPreferencesCard({
  config,
  onSave,
  saving,
}: {
  config: Config;
  onSave: (c: Partial<Config>) => void;
  saving: boolean;
}) {
  const [description, setDescription] = useState(config.job_search_description ?? "");
  const [modes, setModes] = useState<string[]>(config.work_modes ?? []);
  const [minSalary, setMinSalary] = useState(config.min_salary ?? 0);

  const toggleMode = (value: string) =>
    setModes((prev) => (prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]));

  return (
    <Section
      title="Job preferences"
      blurb="Describe the role you're after and which work arrangements you'll accept. These guide how each job is scored for fit."
    >
      <label className="block">
        <span className="block text-xs uppercase tracking-wide text-slate-500">
          What kind of job are you looking for?
        </span>
        <textarea
          rows={5}
          maxLength={2000}
          className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-slate-500"
          placeholder="e.g. Senior backend role in fintech, Go/Python, leading a small team. Prefer mission-driven startups."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <div>
        <span className="block text-xs uppercase tracking-wide text-slate-500">Acceptable work modes</span>
        <div className="mt-2 flex flex-wrap gap-4">
          {WORK_MODES.map((m) => (
            <label key={m.value} className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-600 bg-slate-950"
                checked={modes.includes(m.value)}
                onChange={() => toggleMode(m.value)}
              />
              {m.label}
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">Leave all unchecked for no preference.</p>
      </div>
      <div>
        <NumField
          label="Minimum salary (annual)"
          value={minSalary}
          min={0}
          max={5000000}
          onChange={setMinSalary}
        />
        <p className="mt-1 text-xs text-slate-500">
          0 = no minimum. Jobs whose <em>posted</em> pay tops out below this are skipped instead of
          tailored; postings with no salary listed are unaffected.
        </p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          onClick={() =>
            onSave({ job_search_description: description, work_modes: modes, min_salary: minSalary })
          }
          disabled={saving}
        >
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </Section>
  );
}

export function PromptsCard({
  prompts,
  loading,
  onSave,
  saving,
}: {
  prompts?: PromptDef[];
  loading: boolean;
  onSave: (name: string, content: string) => void;
  saving: boolean;
}) {
  return (
    <Section
      title="Evaluation prompts"
      blurb="Tailor how jobs are scored, tailored, and how profiles are parsed. These apply to all users on the next pipeline run. Reset to default to drop the override."
    >
      {loading && <p className="text-xs text-slate-500">Loading prompts…</p>}
      {prompts?.map((p) => (
        <PromptEditor key={p.name} prompt={p} onSave={onSave} saving={saving} />
      ))}
    </Section>
  );
}

function PromptEditor({
  prompt,
  onSave,
  saving,
}: {
  prompt: PromptDef;
  onSave: (name: string, content: string) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState(prompt.custom ?? prompt.default);
  const isDefault = draft.trim() === prompt.default.trim();
  const isOverridden = prompt.custom != null;

  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-200">
          {prompt.label}
          {isOverridden && <span className="ml-2 text-xs font-normal text-amber-400">customized</span>}
        </span>
        <ConfiguredAt at={prompt.updated_at ?? undefined} />
      </div>
      <textarea
        rows={12}
        spellCheck={false}
        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100 outline-none focus:border-slate-500"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 transition hover:bg-slate-800 disabled:opacity-40"
          disabled={saving || !isOverridden}
          onClick={() => {
            setDraft(prompt.default);
            onSave(prompt.name, "");
          }}
        >
          Reset to default
        </button>
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          disabled={saving || isDefault}
          onClick={() => onSave(prompt.name, draft)}
        >
          {saving ? "Saving…" : "Save prompt"}
        </button>
      </div>
    </div>
  );
}

export function AnthropicCard({
  status,
  onSave,
  saving,
}: {
  status: SecretStatus;
  onSave: (p: Record<string, string | number>) => void;
  saving: boolean;
}) {
  const [key, setKey] = useState("");

  return (
    <Section
      title={`Anthropic API key — ${status.configured ? "configured" : "not set"}`}
      blurb="Global — a single key powers scoring/tailoring for all users. From console.anthropic.com → Settings → Keys."
    >
      <ConfiguredAt at={status.last_updated} />
      <Field label="API key (sk-ant-…)" value={key} onChange={setKey} type="password" />
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          disabled={saving || !key.startsWith("sk-")}
          onClick={() => {
            onSave({ apiKey: key });
            setKey("");
          }}
        >
          {saving ? "Saving…" : "Save key"}
        </button>
      </div>
    </Section>
  );
}
